import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import Fuse from "fuse.js";
import React, { useState } from "react";
import { twJoin } from "tailwind-merge";

import { ManifestIntegrationProvider } from "src/api/connectApi/manifestTypes";
import {
  useConnections,
  useProviderManifest,
} from "src/api/connectApi/queries";
import {
  CaseCreationProviderResource,
  CaseUpdateProviderResource,
  Connection,
  CustomResource,
  DatabaseConnectionResource,
  ManualReviewProviderResource,
  Provider,
  Providers,
  isDatabaseProvider,
} from "src/api/connectApi/types";
import { Flow } from "src/api/flowTypes";
import { Icon } from "src/base-components/Icon";
import { LoadingView } from "src/base-components/LoadingView";
import { SearchBox } from "src/base-components/SearchBox";
import { WorkspaceDataplane } from "src/clients/flow-api/api";
import { isBetaConnection } from "src/connections/CreateConnectionGrid";
import { getProviderDescription } from "src/connections/ProviderResourceProperties";
import {
  isCaseCreationNodeConnection,
  isCaseUpdateNodeConnection,
  isManualReviewNodeConnection,
} from "src/connections/model/inboundWebhook";
import { Node } from "src/constants/NodeDataTypes";
import { NodeIcon as getNodeIcon } from "src/constants/NodeIcons";
import {
  ADD_NODE_GROUP_PROPERTIES,
  CONNECTION_GROUP_PROPERTIES,
  CREATABLE_NODE_PROPERTIES,
  getNodeTitle,
} from "src/constants/NodeProperties";
import {
  SIMPLE_CREATABLE_NODE_TYPES,
  INACTIVE_NODE_TYPES,
  NODE_TYPE,
  PARENT_FLOW_NODE_TYPES,
  AddNodeGroupTypes,
  AddNodeGroupType,
  ConnectionGroupType,
} from "src/constants/NodeTypes";
import { singleFlowIsChildFlow } from "src/flow/Model";
import { rightSidePaneWidthClassname } from "src/flowContainer/RightPane";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { AddConnectionGroupPane } from "src/nodeAdding/AddConnectionGroupPane";
import { AddNodeCard, DisabledDetails } from "src/nodeAdding/AddNodeCard";
import { AddNodeCardSkeleton } from "src/nodeAdding/AddNodeCardSkeleton";
import { AddNodeGroupPane } from "src/nodeAdding/AddNodeGroupPane";
import {
  AddResourcePane,
  AddResourcePaneProps,
} from "src/nodeAdding/AddResourcePane";
import { EmptySearchResult } from "src/nodeAdding/EmptySearchResult";
import { PasteNodesCard } from "src/nodeAdding/PasteNodesCard";
import { AddNodeParams, MAXIMUM_FLOW_NODES } from "src/store/GraphStore";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type AddNodePaneProps = {
  addNode: (params: AddNodeParams) => void;
  onCloseButtonClick: () => void;
  workspace: WorkspaceDataplane;
  flow: Flow;
  nodes: Node[];
};

const creatableNodeTypes = SIMPLE_CREATABLE_NODE_TYPES.filter((type) => {
  return !INACTIVE_NODE_TYPES.includes(type);
});
// A list of connections that are not meant to be used as a node in the flow.
const excludedConnections = ["retool"];
export const AddNodePane: React.FC<AddNodePaneProps> = ({
  addNode,
  onCloseButtonClick,
  workspace,
  flow,
  nodes,
}) => {
  const featureGates = useWorkspaceFeatureGates();
  const [filterTerm, setFilterTerm] = useState<string>("");
  const [selectedGroupType, setSelectedGroupType] = useState<
    AddNodeGroupType | ConnectionGroupType | null
  >(null);
  const [selectedConnection, setSelectedConnection] = useState<Connection>();
  const [selectedManifest, setSelectedManifest] =
    useState<ManifestIntegrationProvider>();
  const connectionResult = useConnections(workspace.base_url!);

  const getDeactivatedInfo = (nodeType: NODE_TYPE): DisabledDetails => {
    if (PARENT_FLOW_NODE_TYPES.includes(nodeType)) {
      if (singleFlowIsChildFlow(flow)) {
        return {
          disabled: true,
          disabledInfo: {
            title: "Unable to use this node",
            body: "This Decision Flow is already called by another Decision Flow.",
          },
        };
      } else if (
        nodes.filter(
          (n) => n.type && PARENT_FLOW_NODE_TYPES.includes(n.type as NODE_TYPE),
        ).length >= (workspace.config?.max_child_flows ?? MAXIMUM_FLOW_NODES)
      ) {
        const maxNodes =
          workspace.config?.max_child_flows ?? MAXIMUM_FLOW_NODES;
        return {
          disabled: true,
          disabledInfo: {
            title: "Unable to use this node",
            body: `You can add up to ${maxNodes} Decision Flow and Loop nodes on your canvas.`,
          },
        };
      }
    }

    return { disabled: false };
  };

  const getFilteredNodeTypes = () => {
    if (filterTerm === "") {
      return creatableNodeTypes;
    }

    const nodesWithDisplayedValue = creatableNodeTypes.map((nodeType) => ({
      nodeType,
      displayedValue: getNodeTitle({ nodeType }),
    }));

    const fuse = new Fuse(nodesWithDisplayedValue, {
      keys: ["displayedValue"],
      threshold: 0.3,
    });
    return fuse.search(filterTerm).map((result) => result.item.nodeType);
  };

  const getNameFilteredConnections = (
    connections: Connection[],
  ): Connection[] => {
    if (filterTerm === "") {
      return connections;
    }
    const fuse = new Fuse(connections, {
      keys: ["name"],
      threshold: 0.3,
    });
    return fuse.search(filterTerm).map((result) => result.item);
  };

  const getFilteredGroupTypes = () => {
    if (filterTerm === "") {
      return AddNodeGroupTypes;
    }

    const groupedNodeTypes = AddNodeGroupTypes.map((groupType) => ({
      groupType,
      displayedValue: getNodeTitle({ nodeType: groupType }),
      description: ADD_NODE_GROUP_PROPERTIES[groupType].ADD_BUTTON_DESCRIPTION,
    }));

    const fuse = new Fuse(groupedNodeTypes, {
      keys: ["displayedValue", "description"],
      threshold: 0.3,
    });

    return fuse.search(filterTerm).map((result) => result.item.groupType);
  };

  const filteredNodeTypes = getFilteredNodeTypes();
  const filteredConnections = getNameFilteredConnections(
    connectionResult.data || [],
  );
  const filteredGroupTypes = getFilteredGroupTypes();

  const shouldShowGroupTypes = featureGates.entitiesEnabled;
  const noSearchResultsFound =
    filteredNodeTypes.length === 0 &&
    filteredConnections.length === 0 &&
    (shouldShowGroupTypes ? filteredGroupTypes.length === 0 : true);

  const onSearchBarInputChanged = (input: string) => setFilterTerm(input);

  const renderConnectApiError = () => (
    <div className="mt-9 flex w-full justify-center text-gray-600 font-inter-semibold-14px">
      Connect-API not available
    </div>
  );

  const renderLoadingConnections = () => (
    <>
      <AddNodeCardSkeleton />
      <AddNodeCardSkeleton />
    </>
  );

  const onConnectionCardClick = (
    connection: Connection,
    connectionManifest?: ManifestIntegrationProvider,
  ) => {
    if (connection.provider === "custom") {
      const nonProbingConnection = connection.resource_configs.filter(
        (resourceConfig) => resourceConfig.resource !== "probe",
      )[0];
      addNode({
        nodeType: NODE_TYPE.CUSTOM_CONNECTION_NODE,
        providerResource: {
          provider: "custom",
          resource: nonProbingConnection.resource as CustomResource,
        },
        connectionId: connection.id,
        resourceConfigId: nonProbingConnection.id,
        mediaKey: connection.configuration.media_key as string,
      });
    } else if (isDatabaseProvider(connection.provider)) {
      const nonProbingConnection = connection.resource_configs.filter(
        (resourceConfig) => resourceConfig.resource !== "probe",
      )[0];
      addNode({
        nodeType: NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: nonProbingConnection.resource as DatabaseConnectionResource,
        },
        connectionId: connection.id,
        resourceConfigId: nonProbingConnection.id,
      });
    } else if (isManualReviewNodeConnection(connection)) {
      addNode({
        nodeType: NODE_TYPE.REVIEW_CONNECTION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: connection.resource_configs[0].resource,
        } as ManualReviewProviderResource,
        connectionId: connection.id,
        resourceConfigId: connection.resource_configs[0].id,
      });
    } else if (isCaseCreationNodeConnection(connection)) {
      addNode({
        nodeType: NODE_TYPE.CASE_CREATION_NODE,
        providerResource: {
          provider: connection.provider,
          resource: connection.resource_configs[0].resource,
        } as CaseCreationProviderResource,
        connectionId: connection.id,
        resourceConfigId: connection.resource_configs[0].id,
      });
    } else if (isCaseUpdateNodeConnection(connection)) {
      addNode({
        nodeType: NODE_TYPE.CASE_UPDATE_NODE,
        providerResource: {
          provider: connection.provider,
          resource: connection.resource_configs[0].resource,
        } as CaseUpdateProviderResource,
        connectionId: connection.id,
        resourceConfigId: connection.resource_configs[0].id,
      });
    } else {
      if (connectionManifest) {
        setSelectedManifest(connectionManifest);
      }
      // Both webhook and "standard" integrations are handled here
      setSelectedConnection(connection);
    }
  };

  const getNodeIconFromConnection = (connection: Connection) => {
    if (isManualReviewNodeConnection(connection)) {
      return getNodeIcon({
        nodeType: NODE_TYPE.REVIEW_CONNECTION_NODE,
      });
    } else if (isCaseCreationNodeConnection(connection)) {
      return getNodeIcon({
        nodeType: NODE_TYPE.CASE_CREATION_NODE,
      });
    } else if (isCaseUpdateNodeConnection(connection)) {
      return getNodeIcon({
        nodeType: NODE_TYPE.CASE_UPDATE_NODE,
      });
    }

    return getNodeIcon({
      nodeType: NODE_TYPE.INTEGRATION_NODE,
      provider: connection.provider,
      mediaKey: connection.configuration.media_key as string,
      size: "md",
    });
  };

  const LegacyProviderConnectionNodeCard = ({
    connection,
  }: {
    connection: Connection;
  }) => {
    return (
      <AddNodeCard
        dataLoc={`add-${connection.provider}-${connection.id}`}
        description={getProviderDescription(connection)}
        isBeta={isBetaConnection(connection.provider)}
        nodeIcon={getNodeIconFromConnection(connection)}
        title={connection.name}
        onClick={() => onConnectionCardClick(connection)}
      />
    );
  };

  const ManifestProviderConnectionNodeCard = ({
    connection,
  }: {
    connection: Connection;
  }) => {
    const workspaceData = useWorkspaceAndOrg();
    const baseUrl = workspaceData.workspace?.base_url;
    const { provider, manifest_version } = connection;
    const manifest = useProviderManifest(
      baseUrl,
      provider,
      manifest_version,
    ).data;
    const description =
      manifest?.description || `Add a ${manifest?.display_name} connection`;
    return (
      <AddNodeCard
        dataLoc={`add-${connection.provider}-${connection.id}`}
        description={description}
        isBeta={isBetaConnection(connection.provider)}
        nodeIcon={getNodeIconFromConnection(connection)}
        title={connection.name}
        onClick={() => onConnectionCardClick(connection, manifest)}
      />
    );
  };

  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  const isConnectionGroupNode = (connection: Connection) =>
    isCaseCreationNodeConnection(connection) ||
    isCaseUpdateNodeConnection(connection);

  const getEnabledCaseConnections = (connections: Connection[]) => {
    return connections.filter((conn) => {
      if (isCaseCreationNodeConnection(conn)) {
        return isCaseManagementEnabled;
      }
      if (isCaseUpdateNodeConnection(conn)) {
        return isCaseManagementEnabled;
      }
      return false;
    });
  };

  const renderConnectionCards = (connections: Connection[]) => {
    const legacyProviderConnections = connections
      .filter((conn) => !conn.manifest_version)
      .filter((conn) => !isConnectionGroupNode(conn));

    const manifestProviderConnections = connections.filter(
      (conn) => conn.manifest_version,
    );
    return (
      <div>
        {isCaseManagementEnabled && (
          <AddNodeCard
            dataLoc="node-group-case_actions"
            description={
              CONNECTION_GROUP_PROPERTIES[ConnectionGroupType.CASE_ACTIONS]
                .ADD_BUTTON_DESCRIPTION
            }
            nodeIcon={getNodeIcon({
              nodeType: ConnectionGroupType.CASE_ACTIONS,
            })}
            title={getNodeTitle({ nodeType: ConnectionGroupType.CASE_ACTIONS })}
            isGroup
            onClick={() =>
              setSelectedGroupType(ConnectionGroupType.CASE_ACTIONS)
            }
          />
        )}
        {getNameFilteredConnections(legacyProviderConnections)
          .filter(
            (connection) =>
              !excludedConnections.includes(connection.provider) &&
              Providers.includes(connection.provider as Provider),
          )
          // If the connection is a custom connection it should only be used with exactly one none-probing resource configured.
          .filter(
            (connection) =>
              connection.provider !== "custom" ||
              connection.resource_configs.length === 2,
          )
          .map((connection) => (
            <LegacyProviderConnectionNodeCard
              key={connection.id}
              connection={connection}
            />
          ))}
        {getNameFilteredConnections(manifestProviderConnections).map(
          (connection) => (
            <ManifestProviderConnectionNodeCard
              key={connection.id}
              connection={connection}
            />
          ),
        )}
      </div>
    );
  };

  if (selectedConnection) {
    const addResourcePaneProps: AddResourcePaneProps = {
      addNode,
      connection: selectedConnection,
      onBackButtonClick: () => {
        setSelectedConnection(undefined);
        setSelectedManifest(undefined);
      },
      onCloseButtonClick,
    };

    if (selectedManifest) {
      addResourcePaneProps.manifest = selectedManifest;
    }

    return <AddResourcePane {...addResourcePaneProps} />;
  }

  if (selectedGroupType) {
    if (selectedGroupType === ConnectionGroupType.CASE_ACTIONS) {
      const caseConnections = getEnabledCaseConnections(
        connectionResult.data || [],
      );

      // If there are no case connections, don't show the group pane.
      if (caseConnections.length === 0) {
        return null;
      }

      return (
        <AddConnectionGroupPane
          connections={caseConnections}
          groupType={selectedGroupType}
          onBackButtonClick={() => {
            setSelectedGroupType(null);
          }}
          onCloseButtonClick={onCloseButtonClick}
          onConnectionClick={onConnectionCardClick}
        />
      );
    }

    return (
      <AddNodeGroupPane
        addNode={addNode}
        groupType={selectedGroupType}
        onBackButtonClick={() => setSelectedGroupType(null)}
        onCloseButtonClick={onCloseButtonClick}
      />
    );
  }

  return (
    <div
      className={twJoin(
        "flex max-h-full flex-col overflow-hidden bg-white pt-6 shadow-lg",
        rightSidePaneWidthClassname,
      )}
    >
      <div className="px-6">
        <div className="mb-4 flex flex-row items-center justify-between">
          <div className="font-inter-semibold-16px">Add a node</div>
          <Icon
            color="text-gray-500 hover:text-gray-700"
            dataLoc="close-add-node-pane"
            icon={faXmark}
            size="xs"
            onClick={onCloseButtonClick}
          />
        </div>
        <SearchBox
          placeholder="Search Node types..."
          onChange={onSearchBarInputChanged}
        />
      </div>
      {noSearchResultsFound ? (
        <EmptySearchResult />
      ) : (
        <div className="shrink overflow-y-auto px-6">
          <div>
            <PasteNodesCard />
            {filteredNodeTypes.map((nodeType) => (
              <AddNodeCard
                key={nodeType}
                dataLoc={`add-${nodeType}`}
                description={
                  CREATABLE_NODE_PROPERTIES[nodeType].ADD_BUTTON_DESCRIPTION
                }
                infoBody={CREATABLE_NODE_PROPERTIES[nodeType].INFO_BODY}
                infoTitle={CREATABLE_NODE_PROPERTIES[nodeType].INFO_TITLE}
                isBeta={CREATABLE_NODE_PROPERTIES[nodeType].IS_BETA}
                nodeIcon={getNodeIcon({
                  nodeType: nodeType,
                  disabled: getDeactivatedInfo(nodeType).disabled,
                })}
                title={getNodeTitle({ nodeType: nodeType })}
                onClick={() => addNode({ nodeType })}
                {...getDeactivatedInfo(nodeType)}
              />
            ))}
            {shouldShowGroupTypes &&
              filteredGroupTypes.map((groupType) => (
                <AddNodeCard
                  key={groupType}
                  dataLoc={`node-group-${groupType}`}
                  description={
                    ADD_NODE_GROUP_PROPERTIES[groupType].ADD_BUTTON_DESCRIPTION
                  }
                  nodeIcon={getNodeIcon({ nodeType: groupType })}
                  title={getNodeTitle({ nodeType: groupType })}
                  isGroup
                  onClick={() => setSelectedGroupType(groupType)}
                />
              ))}
          </div>
          <LoadingView
            queryResult={connectionResult}
            renderErrored={renderConnectApiError}
            renderUpdated={renderConnectionCards}
            renderUpdating={renderLoadingConnections}
          />
        </div>
      )}
    </div>
  );
};
